import { create } from 'zustand'
import { settingsApi, autoReplyRulesApi, api, getErrorMessage } from '@/lib/api'
import { toast } from 'sonner'
import type { UserConfig, SettingsSection, LLMKeyTestResult } from '@/types/settings'
import type { AutoReplyRule, CreateAutoReplyRuleData } from '@/types/senderEmail'

interface CustomAIRecipe {
  id: number
  name: string
  slug: string
  prompt: string
  model: string
  output_mode: 'text' | 'fields' | 'json_schema'
  output_schema: Array<{ name: string; type: string }> | Record<string, any> | null
  custom_context: string | null
}

interface RecipePreviewRequest {
  contact_id: number
  prompt: string
  model: string
  output_mode: string
  output_schema: any
  custom_context?: string
}

interface SettingsState {
  config: UserConfig | null
  llmProviders: Record<string, string[]> | null
  // Whether each provider's model list came live from its API ('live') or is
  // the platform's static fallback list ('static').
  llmModelSources: Record<string, 'live' | 'static'> | null
  autoReplyRules: AutoReplyRule[]
  activeSection: SettingsSection

  isLoadingConfig: boolean
  isLoadingProviders: boolean
  isLoadingRules: boolean
  isSaving: boolean

  setActiveSection: (section: SettingsSection) => void
  fetchConfig: () => Promise<void>
  updateConfig: (data: Record<string, any>, successMessage?: string) => Promise<void>
  revealConfigKey: (field: string) => Promise<string>
  fetchLLMProviders: () => Promise<void>
  testSmtpConnection: () => Promise<any>
  testLLMConnection: (provider: string, model: string, displayName?: string) => Promise<any>
  testLLMKey: (provider: string, apiKey: string) => Promise<LLMKeyTestResult>
  testIntegration: (service: string, apiKey: string, displayName?: string) => Promise<any>
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>
  fetchAutoReplyRules: () => Promise<void>
  createAutoReplyRule: (data: CreateAutoReplyRuleData) => Promise<void>
  updateAutoReplyRule: (id: number, data: Partial<CreateAutoReplyRuleData & { is_active?: boolean }>) => Promise<void>
  deleteAutoReplyRule: (id: number) => Promise<void>

  recipes: CustomAIRecipe[]
  recipesLoading: boolean
  fetchRecipes: () => Promise<void>
  saveRecipe: (data: Partial<CustomAIRecipe>) => Promise<CustomAIRecipe>
  updateRecipe: (id: number, data: Partial<CustomAIRecipe>) => Promise<CustomAIRecipe>
  deleteRecipe: (id: number) => Promise<void>
  previewRecipe: (data: RecipePreviewRequest) => Promise<{ rendered_prompt: string; output_schema: any }>
}

export const useSettingsStore = create<SettingsState>()((set, get) => ({
  config: null,
  llmProviders: null,
  llmModelSources: null,
  autoReplyRules: [],
  activeSection: 'account',

  isLoadingConfig: false,
  isLoadingProviders: false,
  isLoadingRules: false,
  isSaving: false,

  setActiveSection: (section) => set({ activeSection: section }),

  fetchConfig: async () => {
    set({ isLoadingConfig: true })
    try {
      const res = await settingsApi.getConfig()
      set({ config: res.data, isLoadingConfig: false })
    } catch {
      set({ isLoadingConfig: false })
      toast.error('Failed to load settings')
    }
  },

  updateConfig: async (data, successMessage) => {
    set({ isSaving: true })
    try {
      await settingsApi.updateConfig(data)
      // Refresh config to get masked values
      await get().fetchConfig()
      // Callers with a nuance to communicate (e.g. "provider saved but no API
      // key set yet") pass their own message instead of the generic default —
      // see AIModelsSection.saveModelChoice.
      toast.success(successMessage || 'Settings saved')
    } catch (error: any) {
      // Surface the server's reason — validation failures (e.g. a malformed API
      // key) are only actionable if the user can read why the save was rejected.
      toast.error(error.response?.data?.detail || 'Failed to save settings')
    } finally {
      set({ isSaving: false })
    }
  },

  revealConfigKey: async (field) => {
    const res = await settingsApi.revealConfigKey(field)
    return res.data?.value ?? ''
  },

  fetchLLMProviders: async () => {
    set({ isLoadingProviders: true })
    try {
      const res = await settingsApi.getLLMProviders()
      // New shape: { providers, sources }. Old shape (flat provider->models
      // map) still handled so a stale backend doesn't blank the dropdown.
      const data = res.data || {}
      set({
        llmProviders: data.providers ?? data,
        llmModelSources: data.sources ?? null,
        isLoadingProviders: false,
      })
    } catch {
      set({ isLoadingProviders: false })
    }
  },

  testSmtpConnection: async () => {
    try {
      const res = await settingsApi.testSmtpConnection()
      toast.success('SMTP connection successful')
      return res.data
    } catch (error) {
      toast.error(getErrorMessage(error, 'SMTP connection failed'))
      throw new Error('SMTP test failed')
    }
  },

  testLLMConnection: async (provider, model, displayName) => {
    const label = displayName || provider
    try {
      const res = await settingsApi.testLLMConnection(provider, model)
      toast.success(`${label} connection successful`)
      return res.data
    } catch (error) {
      toast.error(getErrorMessage(error, `${label} connection failed`))
      throw new Error('LLM test failed')
    }
  },

  // The field renders the verdict inline next to the input, so no toast here.
  testLLMKey: async (provider, apiKey) => {
    try {
      const res = await settingsApi.testLLMKey(provider, apiKey)
      return res.data
    } catch (error) {
      return {
        status: 'unreachable',
        success: false,
        message: getErrorMessage(error, 'Could not run the key test.'),
      }
    }
  },

  testIntegration: async (service, apiKey, displayName) => {
    // Callers may only have the internal id (e.g. IntegrationsModal's contact
    // picker); the Integrations settings page passes the catalog's full name
    // so the toast reads "HubSpot connected successfully" instead of "hubspot
    // connected successfully".
    const label = displayName || service
    let res
    try {
      res = await settingsApi.testIntegration(service, apiKey)
    } catch (error) {
      toast.error(getErrorMessage(error, `${label} connection failed`))
      throw new Error('Integration test failed')
    }
    // The backend persisted this test's verdict — refresh config so the settings
    // status dot updates (green on success, red on failure).
    await get().fetchConfig()
    if (res.data?.success === false) {
      const msg = res.data.message || 'Connection failed'
      toast.error(`${label}: ${msg}`)
      throw new Error(msg)
    }
    toast.success(`${label} connected successfully`)
    return res.data
  },

  changePassword: async (currentPassword, newPassword) => {
    set({ isSaving: true })
    try {
      await settingsApi.changePassword({ current_password: currentPassword, new_password: newPassword })
      toast.success('Password changed successfully')
    } catch {
      toast.error('Failed to change password')
      throw new Error('Password change failed')
    } finally {
      set({ isSaving: false })
    }
  },

  fetchAutoReplyRules: async () => {
    set({ isLoadingRules: true })
    try {
      const res = await autoReplyRulesApi.getAll()
      set({ autoReplyRules: res.data, isLoadingRules: false })
    } catch {
      set({ isLoadingRules: false })
      toast.error('Failed to load auto-reply rules')
    }
  },

  createAutoReplyRule: async (data) => {
    try {
      const res = await autoReplyRulesApi.create(data)
      set({ autoReplyRules: [res.data, ...get().autoReplyRules] })
      toast.success('Rule created')
    } catch {
      toast.error('Failed to create rule')
      throw new Error('Create rule failed')
    }
  },

  updateAutoReplyRule: async (id, data) => {
    try {
      const res = await autoReplyRulesApi.update(id, data)
      set({ autoReplyRules: get().autoReplyRules.map(r => r.id === id ? res.data : r) })
      toast.success('Rule updated')
    } catch {
      toast.error('Failed to update rule')
      throw new Error('Update rule failed')
    }
  },

  deleteAutoReplyRule: async (id) => {
    try {
      await autoReplyRulesApi.delete(id)
      set({ autoReplyRules: get().autoReplyRules.filter(r => r.id !== id) })
      toast.success('Rule deleted')
    } catch {
      toast.error('Failed to delete rule')
      throw new Error('Delete rule failed')
    }
  },

  recipes: [],
  recipesLoading: false,

  fetchRecipes: async () => {
    set({ recipesLoading: true })
    try {
      const response = await api.get('/custom-ai-recipes')
      set({ recipes: response.data, recipesLoading: false })
    } catch (error) {
      set({ recipesLoading: false })
      throw error
    }
  },

  saveRecipe: async (data) => {
    const response = await api.post('/custom-ai-recipes', data)
    get().fetchRecipes()
    return response.data
  },

  updateRecipe: async (id, data) => {
    const response = await api.put(`/custom-ai-recipes/${id}`, data)
    get().fetchRecipes()
    return response.data
  },

  deleteRecipe: async (id) => {
    await api.delete(`/custom-ai-recipes/${id}`)
    get().fetchRecipes()
  },

  previewRecipe: async (data) => {
    const response = await api.post('/custom-ai-recipes/preview', data)
    return response.data
  },
}))
